import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { CalendarIcon, DocPlusIcon, PlusIcon, WarningIcon } from '../../../assets/icons/icons';
import { Button } from '../../ui/Button/Button';
import { AddressField } from '../../ui/AddressField/AddressField';
import { Modal, ModalActions, ModalNote } from '../../ui/Modal/Modal';
import { cx } from '../../../utils/cx';
import { formatDateTime } from '../../../utils/format';
import {
    EXCEL_MIME_TYPES,
    PROJECT_NAME_MAX,
    PROJECT_NAME_MIN,
} from '../../../entities/project/model/constants';
import styles from './NewProjectModal.module.scss';
import React from 'react';

export interface NewProjectValues {
    name: string;
    address: string;
    postalCode?: string;
    file: File | null;
    useGpt: boolean;
}

interface NewProjectModalProps {
    open: boolean;
    submitting?: boolean;
    serverError?: string | null;
    onClose: () => void;
    onCreate: (values: NewProjectValues) => void;
}

export function NewProjectModal({
    open,
    submitting = false,
    serverError,
    onClose,
    onCreate,
}: NewProjectModalProps) {
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [useGpt, setUseGpt] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [createdAt, setCreatedAt] = useState(() => new Date());

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;

        setName('');
        setAddress('');
        setPostalCode('');
        setFile(null);
        setUseGpt(false);
        setError(null);
        setFileError(null);
        setCreatedAt(new Date());

        const timer = setTimeout(() => inputRef.current?.focus(), 40);
        return () => clearTimeout(timer);
    }, [open]);

    // const isValid = name.trim().length >= PROJECT_NAME_MIN && !fileError;

    const isValid = name.trim().length >= PROJECT_NAME_MIN && !fileError;

    const handleFile = (list: FileList | null): void => {
        const picked = list?.[0];
        if (!picked) return;

        if (list && list.length > 1) {
            setFileError('Можно выбрать только один файл');
            return;
        }

        if (!EXCEL_MIME_TYPES.includes(picked.type)) {
            setFileError('Допустимы только файлы .xls или .xlsx');
            return;
        }

        setFileError(null);
        setFile(picked);
    };

    const submit = (): void => {
        const trimmed = name.trim();

        if (trimmed.length === 0) {
            setError('Поле обязательно для заполнения');
            inputRef.current?.focus();
            return;
        }

        if (trimmed.length < PROJECT_NAME_MIN) {
            setError(`Наименование должно содержать не менее ${PROJECT_NAME_MIN} символов`);
            inputRef.current?.focus();
            return;
        }

        setError(null);
        onCreate({ name: trimmed, address, postalCode, file, useGpt });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        submit();
    };

    const shownError = error ?? serverError ?? null;

    return (
        <Modal
            open={open}
            width={540}
            title="Создание нового проекта"
            description="Заполните обязательное поле и, при необходимости, дополнительные."
            icon={<DocPlusIcon />}
            onClose={onClose}
            headerExtra={
                <div className={styles.dateChip}>
                    <CalendarIcon />
                    <div>
                        <span>Дата создания</span>
                        <b>{formatDateTime(createdAt)}</b>
                    </div>
                </div>
            }
            footer={
                <>
                    <ModalNote>Позиции добавляются после создания проекта</ModalNote>
                    <ModalActions>
                        <Button onClick={onClose} disabled={submitting}>
                            Отмена
                        </Button>
                        <Button
                            variant="primary"
                            icon={<PlusIcon />}
                            disabled={!isValid || submitting}
                            onClick={submit}
                        >
                            {submitting ? 'Создание...' : 'Создать проект'}
                        </Button>
                    </ModalActions>
                </>
            }
        >
            <div className={styles.steps}>
                <div className={styles.step}>
                    <div className={styles.stepBody}>
                        <div className={styles.stepLabel}>
                            Наименование проекта <span className={styles.req}>*</span>
                        </div>
                        <p className={styles.stepHint}>
                            Укажите название так, чтобы проект легко было найти в списке — например,
                            «Ноутбуки для отдела кадров».
                        </p>

                        <input
                            ref={inputRef}
                            className={cx(styles.field, shownError && styles.fieldError)}
                            value={name}
                            maxLength={PROJECT_NAME_MAX}
                            placeholder="Введите наименование проекта"
                            autoComplete="off"
                            aria-label="Наименование проекта"
                            aria-invalid={Boolean(shownError)}
                            onChange={(event) => {
                                setName(event.target.value);
                                if (event.target.value.trim()) setError(null);
                            }}
                            onKeyDown={onKeyDown}
                        />

                        <div className={styles.fieldFoot}>
                            {shownError && (
                                <span className={styles.errMsg}>
                                    <WarningIcon />
                                    {shownError}
                                </span>
                            )}
                            <span className={styles.charCount}>
                                <b>{name.length}</b>/{PROJECT_NAME_MAX}
                            </span>
                        </div>
                    </div>
                </div>

                {/* <div className={styles.step}>
                    <div className={styles.stepBody}>
                        <div className={styles.stepLabel}>Адрес доставки</div>
                        <p className={styles.stepHint}>
                            Нужен для расчёта логистики. Можно указать позже, в карточке проекта.
                        </p>

                        <AddressField
                            value={address}
                            onChange={(nextAddress, nextPostalCode) => {
                                setAddress(nextAddress);
                                setPostalCode(nextPostalCode);
                            }}
                        />
                    </div>
                </div> */}

                {/* <div className={styles.step}>
                    <div className={styles.stepBody}>
                        <div className={styles.stepLabel}>Позиции из файла</div>
                        <p className={styles.stepHint}>
                            Excel-файл по шаблону — позиции подтянутся сразу при создании.
                        </p>

                        {file ? (
                            <div className={styles.fileRow}>
                                <span className={styles.fileName}>{file.name}</span>
                                <button
                                    type="button"
                                    className={styles.fileRemove}
                                    onClick={() => setFile(null)}
                                >
                                    Убрать
                                </button>
                            </div>
                        ) : (
                            <label className={styles.upload}>
                                <input
                                    type="file"
                                    accept=".xls,.xlsx"
                                    hidden
                                    onChange={(event) => handleFile(event.target.files)}
                                />
                                Загрузить по шаблону
                            </label>
                        )}

                        {fileError && (
                            <span className={styles.errMsg}>
                                <WarningIcon />
                                {fileError}
                            </span>
                        )}

                        {file && (
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={useGpt}
                                    onChange={(event) => setUseGpt(event.target.checked)}
                                />
                                Использовать оптимизатор наименований
                            </label>
                        )}
                    </div>
                </div> */}
            </div>
        </Modal>
    );
}